import { AcceptsKey } from "@citychallenge/common";
import "reflect-metadata";

export const INLET_KEY = Symbol("INLETS");
export const OUTLET_KEY = Symbol("OUTLETS");
export const ACCEPTS_KEY = Symbol("INLETS_ACCEPT");

export interface ExposedInlet {
  property: string;
  accepts: AcceptsKey;
  isStatic: boolean;
}

export type ExposedOutletArgument =
  | AcceptsKey
  | {
      type: AcceptsKey; // Type of the argument
      inlet: string; // Refer to another inet
    };

interface ExposedOutletOptions {
  arguments?: {
    [key: string]: ExposedOutletArgument;
  };
  repeatable?: boolean;
}

export interface ExposedOutlet extends ExposedOutletOptions {
  property: string;
}

export type ExposedProperty = ExposedInlet | ExposedOutlet;

function definePropertyMetadata(
  targetSymbol: symbol,
  target: any,
  exposedProperty: ExposedProperty
) {
  // Define propertyKey on the give symbol (inlet or outlet)
  const existingProperties: ExposedProperty[] =
    Reflect.getOwnMetadata(targetSymbol, target) || [];
  existingProperties.push(exposedProperty);

  Reflect.defineMetadata(targetSymbol, existingProperties, target);
}

/**
 * Define property as inlet.
 * @param  accepts Define what kind of types the inlet accepts as a value.
 * @return decorated property
 */
export function Inlet(accepts: AcceptsKey, isStatic = false) {
  return <T extends {}>(target: T, propertyKey: string): any => {
    definePropertyMetadata(INLET_KEY, target, {
      property: propertyKey,
      accepts, // Literal shorthand (ES6 feature),
      isStatic,
    });

    // Define which types this inlet accepts
    Reflect.defineMetadata(ACCEPTS_KEY, accepts, target, propertyKey);
  };
}

/**
 * Get exposed inlets for property constructor
 * @param  propConstructor the constructor
 * @return  String array of exposed inlets
 */
export function getExposedInlets(
  propConstructor: new () => any
): ExposedInlet[] {
  // const gotten = Reflect.getOwnMetadata(INLET_KEY, propConstructor);
  return Reflect.getOwnMetadata(INLET_KEY, propConstructor) || [];
}

/**
 * Define a property outlet. Should only be used with a method property (property or method implementation does not matter)
 * @return decorated property
 */
export function Outlet(options?: ExposedOutletOptions) {
  return <T extends {}>(target: T, propertyKey: string) => {
    definePropertyMetadata(OUTLET_KEY, target, {
      property: propertyKey,
      ...(options || {}),
    });
  };
}

/**
 * Get exposed outlets for property constructor
 * @param  propConstructor the constructor
 * @return  String array of exposed outlets
 */
export function getExposedOutlet(
  propConstructor: new () => any
): ExposedOutlet[] {
  return Reflect.getOwnMetadata(OUTLET_KEY, propConstructor) || [];
}
